import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import { useAppStore } from '@restorant/core';
import type { RestaurantTable, TableKind } from '@restorant/core';
import { useColors } from '../theme';
import { useT } from '../i18n';
import { useRestaurantTables } from '../menu';
import { Chip } from './Chip';
import { Sheet, SheetButton, Toggle } from './Sheet';
import { T } from './Text';
import { TextField } from './TextField';

/**
 * Add, rename, re-seat or retire one table. Writes straight to the store, which
 * queues the change for upload like every other write — so this works offline.
 */
export function TableEditor({
  table,
  visible,
  onClose,
}: {
  /** null = creating a new one. */
  table: RestaurantTable | null;
  visible: boolean;
  onClose: () => void;
}) {
  const colors = useColors();
  const t = useT();
  const all = useRestaurantTables(true);
  const upsert = useAppStore((s) => s.upsertRestaurantTable);
  const remove = useAppStore((s) => s.deleteRestaurantTable);
  const showToast = useAppStore((s) => s.showToast);

  const [id, setId] = useState('');
  const [label, setLabel] = useState('');
  const [labelHi, setLabelHi] = useState('');
  const [labelGu, setLabelGu] = useState('');
  const [seats, setSeats] = useState('');
  const [kind, setKind] = useState<TableKind>('dine_in');
  const [active, setActive] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    setId(table?.id ?? '');
    setLabel(table?.label ?? '');
    setLabelHi(table?.labelHi ?? '');
    setLabelGu(table?.labelGu ?? '');
    setSeats(table?.seats ? String(table.seats) : '');
    setKind(table?.kind ?? 'dine_in');
    setActive(table?.active ?? true);
    setError(null);
    setConfirmDelete(false);
  }, [table, visible]);

  const save = () => {
    const nextId = id.trim();
    if (!nextId) return setError(t('id_required'));
    // The id is the primary key and is written onto every order taken at this
    // table, so it cannot change once the table exists.
    if (!table && all.some((r) => r.id === nextId)) return setError(t('id_taken'));

    const seatCount = parseInt(seats, 10);
    const dineIn = all.filter((r) => r.kind === 'dine_in' && r.id !== nextId);

    upsert({
      id: nextId,
      label: label.trim(),
      labelHi: labelHi.trim(),
      labelGu: labelGu.trim(),
      seats: Number.isFinite(seatCount) && seatCount > 0 ? seatCount : 0,
      kind,
      sortOrder:
        table?.sortOrder ??
        (kind === 'parcel' ? 1000 : Math.max(0, ...dineIn.map((r) => r.sortOrder)) + 10),
      active,
    });
    showToast(t('saved_toast'));
    onClose();
  };

  const destroy = () => {
    if (!table) return;
    if (!confirmDelete) return setConfirmDelete(true);
    remove(table.id);
    showToast(t('deleted_toast'));
    onClose();
  };

  const kinds: { id: TableKind; label: string; icon: 'table' | 'parcel' }[] = [
    { id: 'dine_in', label: t('kind_dine_in'), icon: 'table' },
    { id: 'parcel', label: t('kind_parcel'), icon: 'parcel' },
  ];

  return (
    <Sheet
      visible={visible}
      title={table ? t('edit_floor_table') : t('add_floor_table')}
      onClose={onClose}
      footer={
        <>
          {table ? (
            <SheetButton
              label={confirmDelete ? t('delete_table_confirm') : t('delete')}
              icon="trash"
              tone="danger"
              onPress={destroy}
            />
          ) : null}
          <SheetButton label={t('save')} icon="check" tone="primary" onPress={save} />
        </>
      }
    >
      <View style={{ gap: 6 }}>
        <TextField
          testID="table-id"
          label={t('table_id')}
          value={id}
          onChangeText={setId}
          // Changing it would orphan the order history written against it.
          editable={!table}
          placeholder="9"
        />
        <T size={11.5} color={colors.textFaint}>
          {t('table_id_hint')}
        </T>
      </View>

      <View style={{ gap: 6 }}>
        <TextField
          testID="table-label"
          label={t('table_label')}
          value={label}
          onChangeText={setLabel}
          placeholder={t('table') + ' ' + (id || '9')}
        />
        <T size={11.5} color={colors.textFaint}>
          {t('table_label_hint')}
        </T>
      </View>

      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
        <TextField
          testID="table-label-hi"
          label={t('name_hi')}
          value={labelHi}
          onChangeText={setLabelHi}
        />
        <TextField
          testID="table-label-gu"
          label={t('name_gu')}
          value={labelGu}
          onChangeText={setLabelGu}
        />
      </View>

      <TextField
        testID="table-seats"
        label={t('seats')}
        value={seats}
        onChangeText={(v) => setSeats(v.replace(/[^0-9]/g, ''))}
        keyboardType="number-pad"
        placeholder="0"
      />

      <View style={{ gap: 8 }}>
        <T size={11.5} weight={600} color={colors.textFaint}>
          {t('table_kind')}
        </T>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
          {kinds.map((k) => (
            <Chip
              key={k.id}
              label={k.label}
              icon={k.icon}
              active={kind === k.id}
              onPress={() => setKind(k.id)}
            />
          ))}
        </View>
      </View>

      <Toggle label={t('active_label')} value={active} onChange={setActive} />

      {error ? (
        <T size={12} color={colors.spice}>
          {error}
        </T>
      ) : null}
    </Sheet>
  );
}
